// crewbit-admin/src/components/money/bootstrap/TradePicker.tsx
//
// Tier 2-UI Phase A §WorldClass.4 — admin parallel of mobile TradePicker.
// Web idiom: auto-derived inline display + "Change" toggle that swaps in
// a native <select> with 16 options. Mirrors mobile body decisions
// (collapsed-by-default, fallback hint when industryToTradeKey returned 'other'
// via no match). Hardcoded English per admin convention.

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { palette, radii, spacing } from '../../../styles/tokens';
import { TRADE_LABELS, type TradeKey } from '../../../lib/industryToTradeKey';

const TRADE_ORDER: TradeKey[] = [
  'carpentry',
  'cleaning',
  'electrical',
  'flooring',
  'general_contractor',
  'hvac',
  'landscaping',
  'lawn_care',
  'masonry',
  'mobile_detailing',
  'painting',
  'pest_control',
  'plumbing',
  'roofing',
  'snow_removal',
  'other',
];

interface Props {
  value: TradeKey;
  onChange: (next: TradeKey) => void;
  isFallback?: boolean;
  error?: string | null;
  disabled?: boolean;
}

export default function TradePicker({ value, onChange, isFallback, error, disabled }: Props) {
  const [expanded, setExpanded] = useState(false);

  if (expanded) {
    return (
      <div>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value as TradeKey)}
          disabled={disabled}
          aria-label="Trade"
          autoFocus
          style={{
            width: '100%',
            height: 40,
            paddingInline: spacing.md,
            borderRadius: radii.md,
            border: `1px solid ${error ? palette.red : palette.g300}`,
            background: palette.white,
            color: palette.g800,
            fontSize: 14,
            fontFamily: 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {TRADE_ORDER.map((key) => (
            <option key={key} value={key}>
              {TRADE_LABELS[key]}
            </option>
          ))}
        </select>
        {error ? (
          <div style={{ marginTop: spacing.xs, color: palette.red, fontSize: 12 }}>
            {error}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => !disabled && setExpanded(true)}
        disabled={disabled}
        style={{
          width: '100%',
          minHeight: 50,
          padding: spacing.sm,
          paddingInline: spacing.md,
          borderRadius: radii.md,
          border: `1px solid ${error ? palette.red : palette.g300}`,
          background: palette.white,
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: spacing.sm,
          fontFamily: 'inherit',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: palette.g800 }}>
            {TRADE_LABELS[value]}
          </div>
          <div style={{ fontSize: 12, color: palette.g500, marginTop: 2 }}>
            {isFallback
              ? "Couldn't match your industry — pick the closest trade if relevant."
              : 'Auto-derived from your industry. Click to change.'}
          </div>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          color: palette.accent,
          fontSize: 12,
          fontWeight: 600,
        }}>
          Change
          <ChevronDown size={14} />
        </div>
      </button>
      {error ? (
        <div style={{ marginTop: spacing.xs, color: palette.red, fontSize: 12 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
